import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import type { BenefitSummary, ListItem } from "@yutai/shared"
import { useEffect, useMemo, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { Breadcrumbs } from "../../components/Breadcrumbs.js"
import { SiteFooter } from "../../components/SiteFooter.js"
import { SiteHeader } from "../../components/SiteHeader.js"
import {
  aggregateListYields,
  formatElapsedHoldingPeriod,
  formatYieldPercent,
  getApplicableTier,
  getListItemYields,
} from "../../lib/holdingYield.js"
import { useAllBenefits } from "../../lib/useAllBenefits.js"
import { BulkRegisterModal } from "./BulkRegisterModal.js"
import styles from "./ListDetailPage.module.css"
import { RecommendedBenefitsSection } from "./RecommendedBenefitsSection.js"
import { useLists } from "./useLists.js"

interface ItemRowProps {
  item: ListItem
  benefit: BenefitSummary | undefined
  isHoldingMode: boolean
  companyName: string | undefined
  onRemove: () => void
  onUpdate: (params: {
    acquisitionPrice?: number | null
    acquisitionShares?: number | null
    acquisitionDate?: string | null
    memo?: string
  }) => Promise<void>
  onSaved: () => void
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.17 14.12L5.59 10.7l1.42-1.42L10.83 14.1l6.36-6.36 1.42 1.42-7.78 7.78z"
      />
    </svg>
  )
}

function formatAmount(value: number | null): string {
  return value != null ? value.toLocaleString("ja-JP") : "—"
}

function sanitizeDecimalInput(value: string): string {
  const cleaned = value.replace(/[^\d.]/g, "")
  const [whole, ...fractions] = cleaned.split(".")
  if (fractions.length === 0) return whole
  return `${whole}.${fractions.join("")}`
}

function sanitizeIntegerInput(value: string): string {
  return value.replace(/\D/g, "")
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
      />
    </svg>
  )
}

function WarningIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
      />
    </svg>
  )
}

const BENEFIT_AMOUNT_ESTIMATE_NOTE =
  "保有枚数での優待金額データはまだ未収集のため、最低優待金額で計算しています。"

function BenefitAmountCell({
  amount,
  isEstimated,
}: {
  amount: number | null
  isEstimated: boolean
}) {
  const [tooltipOpen, setTooltipOpen] = useState(false)

  if (amount == null) return <>—</>

  return (
    <span className={styles.benefitAmountWrap}>
      <span>{amount.toLocaleString("ja-JP")}円</span>
      {isEstimated && (
        <span className={styles.warnWrap}>
          <button
            type="button"
            className={styles.warnBtn}
            aria-label="優待金額の計算について"
            aria-expanded={tooltipOpen}
            onClick={() => setTooltipOpen((open) => !open)}
          >
            <WarningIcon />
          </button>
          {tooltipOpen && (
            <span role="tooltip" className={styles.warnTooltip}>
              {BENEFIT_AMOUNT_ESTIMATE_NOTE}
            </span>
          )}
        </span>
      )}
    </span>
  )
}

function BenefitContent({
  benefit,
  sharesInput,
  isHoldingMode,
}: {
  benefit: BenefitSummary | undefined
  sharesInput: string
  isHoldingMode: boolean
}) {
  if (!benefit) return <span className={styles.benefitMuted}>—</span>

  const description = benefit.description || benefit.title
  const sharesNum = Number(sharesInput)
  const hasShares = sharesInput !== "" && sharesNum > 0
  const tier = isHoldingMode && hasShares ? getApplicableTier(benefit.tiers, sharesNum) : null

  return (
    <div className={styles.benefitContent}>
      {description && <p className={styles.benefitDescription}>{description}</p>}
      {tier && (
        <p className={styles.tierHighlight}>
          <span className={styles.tierShares}>{tier.minimumShares.toLocaleString()}株以上</span>
          <span className={styles.tierDesc}>{tier.description}</span>
        </p>
      )}
      {isHoldingMode && hasShares && !tier && benefit.tiers.length > 0 && (
        <p className={styles.benefitMuted}>保有株数では優待ティアに未到達</p>
      )}
      {!description && !tier && <span className={styles.benefitMuted}>—</span>}
    </div>
  )
}

function formatMonths(months: number[]): string {
  return `${[...months].sort((x, y) => x - y).join("・")}月`
}

function MonthLabels({ benefit }: { benefit: BenefitSummary | undefined }) {
  if (!benefit) return null
  const currentMonth = new Date().getMonth() + 1
  const hasRecord = benefit.recordMonths.length > 0
  const hasDelivery = benefit.deliveryMonths.length > 0
  if (!hasRecord && !hasDelivery) return null
  const isRecordMonth = benefit.recordMonths.includes(currentMonth)
  const isDeliveryMonth = benefit.deliveryMonths.includes(currentMonth)

  return (
    <div className={styles.monthLabels}>
      {hasRecord && (
        <span
          className={`${styles.monthLabel} ${styles.recordLabel} ${isRecordMonth ? styles.rainbow : ""}`}
        >
          {isRecordMonth ? "今月" : ""}権利確定 {formatMonths(benefit.recordMonths)}
        </span>
      )}
      {hasDelivery && (
        <span
          className={`${styles.monthLabel} ${styles.deliveryLabel} ${isDeliveryMonth ? styles.rainbow : ""}`}
        >
          {isDeliveryMonth ? "今月" : ""}優待送付 {formatMonths(benefit.deliveryMonths)}
        </span>
      )}
    </div>
  )
}

function ItemRow({
  item,
  benefit,
  isHoldingMode,
  companyName,
  onRemove,
  onUpdate,
  onSaved,
}: ItemRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const [price, setPrice] = useState(item.acquisitionPrice?.toString() ?? "")
  const [shares, setShares] = useState(item.acquisitionShares?.toString() ?? "")
  const [date, setDate] = useState(item.acquisitionDate ?? "")
  const [memo, setMemo] = useState(item.memo)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setPrice(item.acquisitionPrice?.toString() ?? "")
    setShares(item.acquisitionShares?.toString() ?? "")
    setDate(item.acquisitionDate ?? "")
    setMemo(item.memo)
  }, [item])

  const priceNum = Number(price)
  const sharesNum = Number(shares)
  const liveItem = {
    acquisitionPrice: price === "" ? null : priceNum,
    acquisitionShares: shares === "" ? null : sharesNum,
  }
  const yields = getListItemYields(liveItem, benefit, isHoldingMode)
  const elapsed = date ? formatElapsedHoldingPeriod(date) : null

  async function saveIfChanged() {
    const acquisitionPrice = price === "" ? null : priceNum
    const acquisitionShares = shares === "" ? null : sharesNum
    const acquisitionDate = date === "" ? null : date

    const unchanged =
      acquisitionPrice === item.acquisitionPrice &&
      acquisitionShares === item.acquisitionShares &&
      acquisitionDate === (item.acquisitionDate ?? null) &&
      memo === item.memo

    if (unchanged || saving) return

    setSaving(true)
    try {
      await onUpdate({
        acquisitionPrice,
        acquisitionShares,
        acquisitionDate,
        memo,
      })
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  return (
    <tr ref={setNodeRef} style={style} className={styles.row}>
      <td className={styles.dragCell}>
        <button
          type="button"
          className={styles.dragHandle}
          aria-label="並び替え"
          {...attributes}
          {...listeners}
        >
          ⠿
        </button>
      </td>
      <td className={styles.codeCell}>
        <span className={styles.companyCode}>{item.companyId}</span>
      </td>
      <td className={styles.nameCell}>
        {benefit ? (
          <Link to={`/companies/${benefit.id}`} className={styles.companyLink}>
            {benefit.name}
          </Link>
        ) : (
          <span className={styles.companyName}>{companyName ?? "—"}</span>
        )}
        <MonthLabels benefit={benefit} />
      </td>
      <td className={styles.benefitCell}>
        <BenefitContent benefit={benefit} sharesInput={shares} isHoldingMode={isHoldingMode} />
      </td>
      <td className={styles.numCell}>{formatYieldPercent(yields.dividendYield)}</td>
      <td className={styles.numCell}>{formatYieldPercent(yields.benefitYield)}</td>
      <td className={styles.numCell}>{formatYieldPercent(yields.combinedYield)}</td>
      {isHoldingMode && (
        <td className={styles.numCell}>
          <BenefitAmountCell
            amount={yields.benefitAmount}
            isEstimated={yields.benefitAmountEstimated}
          />
        </td>
      )}
      {isHoldingMode && (
        <>
          <td className={styles.inputCell}>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className={styles.cellInput}
              value={price}
              onChange={(e) => setPrice(sanitizeDecimalInput(e.target.value))}
              onBlur={saveIfChanged}
              aria-label="取得単価"
            />
          </td>
          <td className={styles.inputCell}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              className={styles.cellInput}
              value={shares}
              onChange={(e) => setShares(sanitizeIntegerInput(e.target.value))}
              onBlur={saveIfChanged}
              aria-label="取得株数"
            />
          </td>
          <td className={styles.numCell}>{formatAmount(yields.investment)}</td>
          <td className={styles.inputCell}>
            <input
              type="date"
              className={styles.cellInput}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              onBlur={saveIfChanged}
              aria-label="取得日"
            />
          </td>
          <td className={styles.periodCell}>{elapsed ?? "—"}</td>
          <td className={styles.memoCell}>
            <textarea
              className={styles.cellTextarea}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              onBlur={saveIfChanged}
              rows={2}
              aria-label="メモ"
            />
          </td>
        </>
      )}
      <td className={styles.actionCell}>
        <button type="button" className={styles.removeButton} onClick={onRemove} aria-label="削除">
          <TrashIcon />
        </button>
      </td>
    </tr>
  )
}

export function ListDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    lists,
    isLoading,
    renameList,
    toggleHoldingMode,
    removeItem,
    updateItem,
    reorderItems,
    validateCompanyCodes,
    bulkAddItems,
    bulkAddItemsWithHoldings,
  } = useLists()
  const { data: benefits } = useAllBenefits()
  const [nameInput, setNameInput] = useState<string | null>(null)
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [notifyMessage, setNotifyMessage] = useState<string | null>(null)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const list = lists.find((l) => l.id === id)
  const benefitByCompanyId = new Map((benefits ?? []).map((b) => [b.id, b]))

  const totals = useMemo(() => {
    if (!list || list.items.length === 0) return null
    const rowYields = list.items.map((item) => {
      const benefit = benefitByCompanyId.get(item.companyId)
      return getListItemYields(item, benefit, list.isHoldingMode)
    })
    const shareCounts = list.items.map((item) => item.acquisitionShares ?? 0)
    return aggregateListYields(rowYields, shareCounts)
  }, [list, benefitByCompanyId])

  useEffect(() => {
    if (searchParams.get("bulk") === "1" && list) {
      setBulkModalOpen(true)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, list, setSearchParams])

  useEffect(() => {
    if (!notifyMessage) return
    const timer = window.setTimeout(() => setNotifyMessage(null), 2400)
    return () => window.clearTimeout(timer)
  }, [notifyMessage])

  function showNotify(message: string) {
    setNotifyMessage(message)
  }

  function handleItemSaved() {
    showNotify("保存しました")
  }

  async function handleRemoveItem(item: ListItem, companyName: string | undefined) {
    if (!list) return
    const label = companyName ? `${item.companyId} ${companyName}` : item.companyId
    if (!window.confirm(`${label} をリストから削除しますか？`)) return
    try {
      await removeItem(list.id, item.id)
    } catch (e) {
      console.error(e)
      showNotify("削除に失敗しました")
      return
    }
    showNotify("削除しました")
  }

  async function handleDragEnd(e: DragEndEvent) {
    if (!list) return
    const { active, over } = e
    if (!over || active.id === over.id) return
    const oldIndex = list.items.findIndex((i) => i.id === active.id)
    const newIndex = list.items.findIndex((i) => i.id === over.id)
    const reordered = [...list.items]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    await reorderItems(
      list.id,
      reordered.map((i) => i.id)
    )
    showNotify("並び替えを保存しました")
  }

  if (isLoading) {
    return <div className={styles.notice}>読み込み中...</div>
  }
  if (!list) {
    return (
      <div className={styles.page}>
        <SiteHeader />
        <Breadcrumbs
          className={styles.breadcrumbs}
          items={[
            { label: "トップ", href: "/" },
            { label: "マイリスト", href: "/lists" },
            { label: "リストが見つかりません" },
          ]}
        />
        <main className={styles.main}>
          <p className={styles.notice}>リストが見つかりませんでした。</p>
          <Link to="/lists">マイリストに戻る</Link>
        </main>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <SiteHeader />
      <Breadcrumbs
        className={styles.breadcrumbs}
        items={[
          { label: "トップ", href: "/" },
          { label: "マイリスト", href: "/lists" },
          { label: list.name },
        ]}
      />

      {notifyMessage && (
        <output className={styles.notify} aria-live="polite">
          <span className={styles.notifyIcon}>
            <CheckIcon />
          </span>
          <span className={styles.notifyText}>{notifyMessage}</span>
        </output>
      )}

      <main className={styles.main}>
        <input
          type="text"
          className={styles.nameInput}
          value={nameInput ?? list.name}
          onChange={(e) => setNameInput(e.target.value)}
          onBlur={() => {
            if (nameInput?.trim() && nameInput !== list.name) {
              renameList(list.id, nameInput.trim())
            }
            setNameInput(null)
          }}
        />

        <label className={styles.holdingToggle}>
          <input
            type="checkbox"
            checked={list.isHoldingMode}
            onChange={(e) => toggleHoldingMode(list.id, e.target.checked)}
          />
          保有銘柄モード（取得単価・株数・日付・メモを記録し、実際の利回りを表示）
        </label>

        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.bulkRegisterButton}
            onClick={() => setBulkModalOpen(true)}
          >
            一括銘柄登録
          </button>
        </div>

        {list.items.length === 0 && (
          <p className={styles.notice}>まだ銘柄がありません。一覧ページの☆から追加できます。</p>
        )}

        {list.items.length > 0 && (
          <div className={styles.tableWrapper}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.dragCell} aria-label="並び替え" />
                    <th>コード</th>
                    <th>銘柄名</th>
                    <th>優待内容</th>
                    <th>配当利回り</th>
                    <th>優待利回り</th>
                    <th>総合利回り</th>
                    {list.isHoldingMode && <th>優待金額</th>}
                    {list.isHoldingMode && (
                      <>
                        <th>取得単価</th>
                        <th>取得株数</th>
                        <th>投資額</th>
                        <th>取得日</th>
                        <th>保有期間</th>
                        <th>メモ</th>
                      </>
                    )}
                    <th className={styles.actionCell} />
                  </tr>
                </thead>
                <SortableContext
                  items={list.items.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {list.items.map((item) => {
                      const benefit = benefitByCompanyId.get(item.companyId)
                      const companyName = benefit?.name
                      return (
                        <ItemRow
                          key={item.id}
                          item={item}
                          benefit={benefit}
                          isHoldingMode={list.isHoldingMode}
                          companyName={companyName}
                          onRemove={() => handleRemoveItem(item, companyName)}
                          onUpdate={(params) => updateItem(list.id, item.id, params)}
                          onSaved={handleItemSaved}
                        />
                      )
                    })}
                    {totals && (
                      <tr className={styles.totalRow}>
                        <td className={styles.dragCell} />
                        <td colSpan={3} className={styles.totalLabel}>
                          合計
                        </td>
                        <td className={styles.numCell}>
                          {formatYieldPercent(totals.dividendYield)}
                        </td>
                        <td className={styles.numCell}>
                          {formatYieldPercent(totals.benefitYield)}
                        </td>
                        <td className={styles.numCell}>
                          {formatYieldPercent(totals.combinedYield)}
                        </td>
                        {list.isHoldingMode && (
                          <td className={styles.numCell}>
                            {totals.totalBenefitAmount != null
                              ? `${formatAmount(totals.totalBenefitAmount)}円`
                              : "—"}
                          </td>
                        )}
                        {list.isHoldingMode && (
                          <>
                            <td className={styles.numCell}>—</td>
                            <td className={styles.numCell}>{formatAmount(totals.totalShares)}</td>
                            <td className={styles.numCell}>
                              {formatAmount(totals.totalInvestment)}
                            </td>
                            <td colSpan={3} />
                          </>
                        )}
                        <td className={styles.actionCell} />
                      </tr>
                    )}
                  </tbody>
                </SortableContext>
              </table>
            </DndContext>
          </div>
        )}

        {list.items.length > 0 && benefits && (
          <RecommendedBenefitsSection
            listItems={list.items}
            isHoldingMode={list.isHoldingMode}
            allBenefits={benefits}
          />
        )}
      </main>

      <BulkRegisterModal
        open={bulkModalOpen}
        listName={list.name}
        onClose={() => setBulkModalOpen(false)}
        onValidate={(codes) => validateCompanyCodes(list.id, codes)}
        onRegister={(companyIds) => bulkAddItems(list.id, companyIds)}
        onRegisterHoldings={(items) => bulkAddItemsWithHoldings(list.id, items)}
      />

      <SiteFooter />
    </div>
  )
}
